import React, { FC, useCallback, useMemo } from 'react'
import { Formik } from 'formik'
import * as config from 'config'
import ActionButton from 'uiComponents/buttons'
import RadioFieldFormik from 'uiComponents/input/radio/radioFieldFormik'
import { ModalDialog, ModalDialogTitle } from 'uiComponents/popups/modal'
import { useDownloadLink } from 'orders/orders/utils'
import { CancelButton } from 'uiComponents/buttons/cancelButton'
import { useScheduleAsyncExportMutation } from 'orders/reduxQueries'
import './cartExportModal.scss'
import { useAppSelector } from 'store/hooks'
import { useNavigation } from 'hocs'
import { dateRangeFromQuery } from 'dateRanges'
import { differenceInDays, format } from 'date-fns'
import { getFiltersFromUrl } from 'uiComponents/filter/filterHelpers'
import { camelToSnakeCase, convertToSnakeCase } from 'utils/camelToSnake'
import { SortProps } from 'uiComponents/table/reactTable'
import { showErrorMessage, showSuccessMessage } from 'uiComponents/message'
import { useTranslation } from 'translations'

const backofficeEndpoint = config.getRequired('backoffice-endpoint')

interface FormValues {
    format: 'csv' | 'xlsx' | 'pdf'
    dateSelection: 'all' | 'current'
}

const InitialExportValues: FormValues = {
    format: 'csv',
    dateSelection: 'all',
}

export type ExportReservationsPayload = {
    search: string
    export_format: string
    status: string
    filter_by_date: string
    ordering: string | undefined
    date_from: string
    date_to: string
    include: string
    locations: string
}

type CartExportModalProps = {
    onClose: () => void
    sort: SortProps | null
}

const CartExportModal: FC<CartExportModalProps> = ({ onClose, sort }) => {
    const { t } = useTranslation()
    const navigation = useNavigation()
    const accountSlug = useAppSelector((state) => state.preferences.activeAccount ?? '')
    const downloadLink = useDownloadLink()
    const [scheduleAsyncExport] = useScheduleAsyncExportMutation()
    const query = navigation.query()

    const statusFilter = useMemo(() => getFiltersFromUrl('reservation_status'), [navigation])
    const locationFilter = useMemo(() => getFiltersFromUrl('location_id'), [navigation])

    const dateFilterParams = useMemo(() => {
        const dateRange = dateRangeFromQuery(query, 'last30days')

        return {
            dateFrom: format(dateRange.from, 'yyyy-MM-dd'),
            dateTo: format(dateRange.to, 'yyyy-MM-dd'),
            filterByDate: query.dateRangeType ?? 'created_at',
        }
    }, [query])

    const getDownloadLinkProps = useCallback(
        (format: string): ExportReservationsPayload => {
            const orderKey = camelToSnakeCase(sort?.prop || 'created_at')
            const ordering = !orderKey ? undefined : sort?.direction === 'asc' ? orderKey : `-${orderKey}`

            return {
                search: query.search ?? query.q ?? '',
                export_format: format,
                status: statusFilter,
                locations: locationFilter,
                include: 'customer,urls,items.product_name,comment',
                ordering,
                ...convertToSnakeCase(dateFilterParams),
            }
        },
        [query],
    )

    const getDownloadLink = (dateRangeMoreThanOneDay: boolean) => {
        return `${backofficeEndpoint}dashboard_api/accounts/${accountSlug}/export/carts/${
            dateRangeMoreThanOneDay ? 'schedule/' : ''
        }`
    }

    const onSubmit = async (values: FormValues) => {
        try {
            const dateRangeMoreThanOneDay =
                differenceInDays(new Date(dateFilterParams.dateTo), new Date(dateFilterParams.dateFrom)) >= 1

            const endpoint = getDownloadLink(dateRangeMoreThanOneDay)
            if (dateRangeMoreThanOneDay) {
                const payload = getDownloadLinkProps(values.format)
                await scheduleAsyncExport({ endpoint, payload })
                showSuccessMessage(t('orders.cart.export.scheduledSuccess'))
                onClose()
            } else {
                const payload = getDownloadLinkProps(values.format)
                await downloadLink(endpoint, payload)
                showSuccessMessage(t('orders.cart.export.downloadSuccess'))
                onClose()
                return
            }
        } catch {
            showErrorMessage(t('orders.cart.export.error'))
        }
    }

    return (
        <ModalDialog onDismiss={onClose} interactive fromTop="20%" className="cart-export-modal">
            <ModalDialogTitle>{t('orders.cart.export.title')}</ModalDialogTitle>
            <Formik initialValues={InitialExportValues} onSubmit={onSubmit} enableReinitialize>
                {({ values, handleSubmit }) => (
                    <form onSubmit={handleSubmit}>
                        <div className="questions-container">
                            <div className="format">
                                <h6>{t('orders.cart.export.format')}</h6>
                                <div className="format-row">
                                    <RadioFieldFormik
                                        variant="inline-reverse"
                                        name="format"
                                        value="csv"
                                        label=".CSV"
                                        checkMarkSize="medium"
                                        className={values.format === 'csv' ? 'selected' : ''}
                                        id="export-csv"
                                    />
                                    <RadioFieldFormik
                                        variant="inline-reverse"
                                        name="format"
                                        value="xlsx"
                                        label=".XLSX"
                                        checkMarkSize="medium"
                                        className={values.format === 'xlsx' ? 'selected' : ''}
                                        id="export-xlsx"
                                    />
                                    <RadioFieldFormik
                                        variant="inline-reverse"
                                        name="format"
                                        value="pdf"
                                        label=".PDF"
                                        checkMarkSize="medium"
                                        className={values.format === 'pdf' ? 'selected' : ''}
                                        id="export-pdf"
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="buttons-container">
                            <CancelButton size="large" secondary onClick={onClose} data-testid="export-cancel-button">
                                {t('common.cancel')}
                            </CancelButton>
                            <ActionButton size="large" kind="default" type="submit" data-testid="export-exports-button">
                                {t('orders.cart.exportButton')}
                            </ActionButton>
                        </div>
                    </form>
                )}
            </Formik>
        </ModalDialog>
    )
}

export default CartExportModal
